import {
  DocumentReference,
  Timestamp,
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
} from "firebase/firestore";
import { ChatMessage, ChatStatus, ChatSupport, ChatSupportCollectionName } from "../entities/chat-support.entity";
import { Repository } from "../firebase/firestore/repository";
import { RepoistoryFilter } from "../types/util.types";
import { ActiveChatMessage, ChatSummary, NewMessageBeforeSave } from "../types/chat-support/chat-support.type";
import { buildFilterQuery_v2 } from "../firebase/firestore/filterQueryBuilder";
import { userRepository } from "./user.repository";

export class ChatSupportRepository extends Repository<ChatSupport> {
  constructor() {
    super(ChatSupportCollectionName);
  }

  async getChatsByState(filter: RepoistoryFilter<ChatSupport>, status?: ChatStatus): Promise<ChatSummary[]> {
    const snapShots = await this.getChatsByStatus(filter, status);
    return Promise.all(
      snapShots.docs.map(async (doc) => {
        const chatData = doc.data() as ChatSupport;
        const id = doc.id;
        const docRef = doc.ref;
        const chatDoc = { id, docRef, ...chatData };
        const [chatSummary] = await this.getChatSummariesWithLastMessageAndUserData([chatDoc]);
        return chatSummary;
      })
    );
  }
  async getChatCountsByState(status?: ChatStatus): Promise<number> {
    const filter: RepoistoryFilter<ChatSupport> = {};
    if (typeof status === "number") {
      filter.where = [["status", "==", status]];
    }
    const query = buildFilterQuery_v2(this.collection, filter.where);
    return this.countByQuery(query);
  }

  private getChatsByStatus({ limit = 20, offsetDocId, orderBy }: RepoistoryFilter<ChatSupport>, status?: ChatStatus) {
    if (typeof status === "number") {
      return this.findByFilter({
        where: [["status", "==", status]],
        limit,
        offsetDocId,
        orderBy,
      });
    }
    return this.findByFilter({
      limit,
      offsetDocId,
      orderBy,
    });
  }

  async saveNewMessage(chatDoc: ChatSummary, newMessage: NewMessageBeforeSave) {
    const messagesRef = collection(chatDoc.docRef, "messages");
    delete newMessage.tempId;
    const newMessageData: Omit<ChatMessage, "id"> = {
      ...newMessage,
      createdAt: serverTimestamp() as Timestamp,
    };

    await addDoc(messagesRef, newMessageData);
    const unreadCountUser: number = (chatDoc.unreadCountUser ?? 0) + 1;
    const unreadCountSupport = chatDoc.unreadCountSupport ?? 0;

    await this.update(chatDoc.id, {
      unreadCountSupport,
      unreadCountUser,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  async fetchOldMessagesByRef(
    chatDocRef: DocumentReference,
    lastMessageDocId: string,
    limitedDocs: number
  ): Promise<Omit<ActiveChatMessage, "student">[]> {
    const messagesRef = collection(chatDocRef, "messages");
    const docSnapshot = await this.getDocByCollectionAndDocId(messagesRef, lastMessageDocId);
    const lastMessageQuery = query(
      messagesRef,
      orderBy("createdAt", "desc"),
      startAfter(docSnapshot),
      limit(limitedDocs)
    );
    const messageSnapshot = await getDocs(lastMessageQuery);

    const fetchedDocs: Omit<ActiveChatMessage, "student">[] = messageSnapshot.docs.map((doc) =>
      this.mapToDataWithIdAndRef<Omit<ActiveChatMessage, "student">>(doc)
    );
    return fetchedDocs;
  }

  async getLastMessageByRef(chatDocRef: DocumentReference) {
    const messagesRef = collection(chatDocRef, "messages");
    const lastMessageQuery = query(messagesRef, orderBy("createdAt", "desc"), limit(1));
    const messageSnapshot = await getDocs(lastMessageQuery);
    const message = messageSnapshot.docs[0].data() as ChatMessage;
    message.id = messageSnapshot.docs[0].id;
    return message;
  }

  async getChatSummariesWithLastMessageAndUserData(
    chats: Omit<ChatSummary, "lastMessage" | "user">[]
  ): Promise<ChatSummary[]> {
    const chatSummaryPromises = chats.map(async (doc) => {
      const lastMessage = await this.getLastMessageByRef(doc.docRef);

      const user = await userRepository.findById(doc.id);
      console.log("user ", user);
      const chatSummary: ChatSummary = {
        ...doc,
        lastMessage,
        user: {
          email: user?.email!,
          name: user?.name!,
          pictureURL: user?.pictureURL!,
          userId: user?.id!,
          roles: user?.roles!,
        },
      };
      return chatSummary;
    });
    const summaries = await Promise.all(chatSummaryPromises);
    return summaries;
  }

  updateChatSupportStatus(docId: string, status: ChatStatus) {
    return this.update(docId, { status });
  }
  updateChatSupportReadCounter(docId: string, unreadCountSupport: number, unreadCountUser: number) {
    return this.update(docId, {
      unreadCountSupport,
      unreadCountUser,
    });
  }

  // getChatMessage //before pagination
}
